import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TruckIcon,
  ArrowRightOnRectangleIcon,
  ChartBarSquareIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  MapIcon,
  Cog6ToothIcon,
  BellIcon,
  MapPinIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import useUserRole from '../../hooks/useUserRole';
import { useDriver } from '../../contexts/DriverContext';

const Sidebar = ({ userData, handleLogout, isOpen, toggleSidebar, hideOnMobile = false }) => {
  const location = useLocation();
  const sidebarRef = useRef(null);
  const { t } = useTranslation();
  const { userPermissions } = useUserRole();
  const { refreshDriverAccount } = useDriver();

  // Prevent background scrolling when sidebar is open on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 1024 && !hideOnMobile) {
      // Save current scroll position
      const scrollY = window.scrollY;
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        // Unlock body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, hideOnMobile]);

  // Handle clicks outside of sidebar
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if sidebar is open and we're on mobile (window width < 1024px)
      if (isOpen && window.innerWidth < 1024 &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)) {
        toggleSidebar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, toggleSidebar]);

  // Function to handle link clicks on mobile
  const handleLinkClick = () => {
    // Refresh driver account when navigating
    refreshDriverAccount();

    if (window.innerWidth < 1024) {
      toggleSidebar();
    }
  };

  // Function to check if the current path is active
  const isActive = (path) => {
    // For dashboard, require exact match
    if (path === '/driver') {
      return location.pathname === '/driver' || location.pathname === '/driver/';
    }
    // For other routes, check if the current path starts with this path
    return location.pathname.startsWith(path);
  };

  // Function to get the class name based on active status
  const getLinkClassName = (path) => {
    const baseClasses = "flex items-center p-3 rounded-2xl group  ";
    const activeClasses = "bg-gradient-to-r from-purple-600 to-purple-700 dark:from-purple-600 dark:to-purple-700 text-white font-semibold shadow-lg shadow-purple-500/30";
    const inactiveClasses = "text-slate-700 dark:text-slate-300 hover:bg-purple-50/80 dark:hover:bg-purple-900/30 hover:text-purple-700 dark:hover:text-purple-300 hover:shadow-sm";

    return `${baseClasses} ${isActive(path) ? activeClasses : inactiveClasses}`;
  };

  // Function to get icon class name based on active status
  const getIconClassName = (path) => {
    const baseClasses = "h-5 w-5 mr-3 transition-colors";
    const activeClasses = "text-white";
    const inactiveClasses = "text-slate-500 dark:text-slate-400 group-hover:text-purple-600 dark:group-hover:text-purple-400";

    return `${baseClasses} ${isActive(path) ? activeClasses : inactiveClasses}`;
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && !hideOnMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden animate-fade-in"
          onClick={toggleSidebar}
          style={{
            touchAction: 'none',
            overscrollBehavior: 'none'
          }}
        ></div>
      )}

      <div
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full shadow-xl ${hideOnMobile ? 'hidden lg:block' : (isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0')} z-50 lg:z-0 w-[88%] max-w-[280px] sm:w-64 bg-slate-100 dark:text-white text-black dark:bg-slate-900 flex flex-col lg:block transition-transform ease-in-out`}
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          height: '100dvh',
          overscrollBehavior: 'contain'
        }}
      >

        {/* Main Content Container */}
        <div className="flex flex-col h-full">
          {/* Profile Section - Fixed at top */}
          <div
            className="flex flex-col mb-4 lg:mb-6 mt-4 lg:mt-10 flex-shrink-0 px-4 lg:px-5"
            style={{
              paddingTop: 'max(1rem, calc(env(safe-area-inset-top, 0px) + 1rem))',
            }}
          >
            <div className="flex items-center justify-center mb-3 lg:mb-4">
              <div className="relative">
                <div className="w-14 h-14 lg:w-14 lg:h-14 rounded-full bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700 dark:from-purple-400 dark:via-purple-500 dark:to-purple-600 flex items-center justify-center border-2 border-purple-500 dark:border-purple-400 shadow-lg">
                  <span className="text-white text-xl lg:text-2xl font-bold">
                    {userData.name
                      .split(' ')
                      .map(word => word[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </span>
                </div>
                <div className="absolute bottom-1 right-1 w-3 h-3 lg:w-4 lg:h-4 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-800"></div>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm lg:text-base font-medium text-slate-900 dark:text-white truncate">{userData.name}</p>
              <p className="text-slate-500 dark:text-slate-400 text-xs lg:text-sm truncate">{userData.email}</p>
            </div>
          </div>

          {/* Scrollable Navigation */}
          <div
            className="flex-1 overflow-y-auto px-4 lg:px-5 min-h-0"
            style={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain'
            }}
          >
            <nav className="pb-4">
              <ul className="flex flex-col space-y-1 lg:space-y-2">
                <li>
                  <Link to="/driver" className={getLinkClassName('/driver')} onClick={handleLinkClick}>
                    <ChartBarSquareIcon className={getIconClassName('/driver')} />
                    <span>{t('sidebar.dashboard')}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/driver/cars" className={getLinkClassName('/driver/cars')} onClick={handleLinkClick}>
                    <TruckIcon className={getIconClassName('/driver/cars')} />
                    <span>{t('sidebar.yourCars')}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/driver/parking" className={getLinkClassName('/driver/parking')} onClick={handleLinkClick}>
                    <MapPinIcon className={getIconClassName('/driver/parking')} />
                    <span>{t('sidebar.parking')}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/driver/appointments-map" className={getLinkClassName('/driver/appointments-map')} onClick={handleLinkClick}>
                    <MapIcon className={getIconClassName('/driver/appointments-map')} />
                    <span>{t('sidebar.onlineAppointment')}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/driver/apps" className={getLinkClassName('/driver/apps')} onClick={handleLinkClick}>
                    <CalendarIcon className={getIconClassName('/driver/apps')} />
                    <span>{t('sidebar.appointmentsList')}</span>
                  </Link>
                </li>
                {/* <li>
                  <Link to="/driver/notifications" className={getLinkClassName('/driver/notifications')} onClick={handleLinkClick}>
                    <BellIcon className={getIconClassName('/driver/notifications')} />
                    <span>{t('sidebar.notifications')}</span>
                  </Link>
                </li> */}
                <li>
                  <Link to="/driver/verify-km" className={getLinkClassName('/driver/verify-km')} onClick={handleLinkClick}>
                    <ClipboardDocumentListIcon className={getIconClassName('/driver/verify-km')} />
                    <span>{t('sidebar.verifyKm')}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/driver/payment-history" className={getLinkClassName('/driver/payment-history')} onClick={handleLinkClick}>
                    <CreditCardIcon className={getIconClassName('/driver/payment-history')} />
                    <span>{t('sidebar.paymentHistory')}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/driver/settings" className={getLinkClassName('/driver/settings')} onClick={handleLinkClick}>
                    <Cog6ToothIcon className={getIconClassName('/driver/settings')} />
                    <span>{t('sidebar.settings')}</span>
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          {/* Bottom Section (Logout) - Fixed at bottom */}
          <div
            className="flex-shrink-0 px-4 lg:px-5"
            style={{
              paddingBottom: 'max(1rem, calc(env(safe-area-inset-bottom, 0px) + 1rem))',
            }}
          >
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mb-2"></div>
            <button
              onClick={(e) => {
                handleLogout();
                if (window.innerWidth < 1024) {
                  toggleSidebar();
                }
              }}
              className="flex items-center w-full p-3 text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-slate-700 hover:text-rose-700 dark:hover:text-rose-300 rounded-lg group"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 text-rose-500 dark:text-rose-400 group-hover:text-rose-700 dark:group-hover:text-rose-300" />
              <span>{t('sidebar.logout')}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
